import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dbGet, dbSet, dbUpdate, dbRemove, dbPush, subscribeToPath } from '../config/firebase';
import { useEffect, useState, useCallback } from 'react';
import {
  User,
  GTWallet,
  GTTransaction,
  AdminStats,
  Report,
  StoreItem,
  StoreRedemption,
  GTPurchase,
  TokenPackage,
  SlipSubmission,
  SubmissionStatus,
  StoreCategory,
  RedemptionType,
  RedemptionStatus
} from '../types';
import { DEFAULT_TOKEN_PACKAGES } from '../config/stripe';
import { useTestMode, TEST_MODE_PREFIX } from '../contexts/TestModeContext';

// Production prefix for v1 paths
const PROD_PREFIX = 'v1';

// Helper to get test-mode aware path with v1 prefix
function getTestModePath(path: string, isTestMode: boolean): string {
  // Don't prefix paths that are already prefixed
  if (path.startsWith(PROD_PREFIX) || path.startsWith(TEST_MODE_PREFIX) || path.startsWith('admins')) {
    return path;
  }
  const prefix = isTestMode ? TEST_MODE_PREFIX : PROD_PREFIX;
  return `${prefix}/${path}`;
}

// Real-time subscription hook
export function useRealtimeData<T>(path: string, enabled: boolean = true) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = subscribeToPath(
      path,
      (snapshot) => {
        setData(snapshot as T);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [path, enabled]);

  return { data, loading, error };
}

// User Queries
export function useUser(userId: string | undefined) {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null;
      const snapshot = await dbGet(`v1/users/profiles/${userId}`);
      return snapshot.val() as User | null;
    },
    enabled: !!userId
  });
}

export function useAllUsers() {
  const { isTestMode } = useTestMode();
  const path = getTestModePath('users/profiles', isTestMode);

  return useQuery({
    queryKey: ['users', isTestMode],
    queryFn: async () => {
      const snapshot = await dbGet(path);
      const data = snapshot.val();
      if (!data) return [];
      return Object.values(data) as User[];
    }
  });
}

// Admin list (from v1/users/admins path)
export function useAdminsList() {
  return useRealtimeData<Record<string, boolean>>(`${PROD_PREFIX}/users/admins`, true);
}

// GT Wallet Queries (test mode aware)
export function useGTWallet(userId: string | undefined) {
  const { isTestMode } = useTestMode();
  const path = userId ? getTestModePath(`wallet/balances/${userId}`, isTestMode) : '';
  return useRealtimeData<GTWallet>(path, !!userId);
}

// Real-time GT Transactions hook (test mode aware)
export function useGTTransactions(userId: string | undefined) {
  const { isTestMode } = useTestMode();
  const [data, setData] = useState<GTTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const path = userId ? getTestModePath(`wallet/transactions/${userId}`, isTestMode) : '';

  useEffect(() => {
    if (!userId || !path) {
      setData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const unsubscribe = subscribeToPath(
      path,
      (snapshot) => {
        if (snapshot) {
          const transactions = Object.values(snapshot) as GTTransaction[];
          setData(transactions);
        } else {
          setData([]);
        }
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, path]);

  return { data, isLoading, error };
}

export function useAllGTWallets() {
  const { isTestMode } = useTestMode();
  const path = getTestModePath('wallet/balances', isTestMode);

  return useQuery({
    queryKey: ['allGTWallets', isTestMode],
    queryFn: async () => {
      const snapshot = await dbGet(path);
      const data = snapshot.val();
      if (!data) return {};
      return data as Record<string, GTWallet>;
    }
  });
}

// GT Purchases
export function useGTPurchases(userId: string | undefined) {
  const { isTestMode } = useTestMode();
  return useQuery({
    queryKey: ['gtPurchases', userId, isTestMode],
    queryFn: async () => {
      if (!userId) return [];
      const snapshot = await dbGet(getTestModePath(`wallet/purchasesByUser/${userId}`, isTestMode));
      const purchaseIds = snapshot.val();
      if (!purchaseIds) return [];

      const purchases: GTPurchase[] = [];
      for (const purchaseId of Object.keys(purchaseIds)) {
        const purchaseSnapshot = await dbGet(getTestModePath(`wallet/purchases/${purchaseId}`, isTestMode));
        if (purchaseSnapshot.exists()) {
          purchases.push(purchaseSnapshot.val());
        }
      }
      return purchases;
    },
    enabled: !!userId
  });
}

// All GT Purchases (Admin)
export function useAllGTPurchases() {
  const { isTestMode } = useTestMode();
  const path = getTestModePath('wallet/purchases', isTestMode);

  return useQuery({
    queryKey: ['allGTPurchases', isTestMode],
    queryFn: async () => {
      const snapshot = await dbGet(path);
      const data = snapshot.val();
      if (!data) return [];
      return Object.values(data) as GTPurchase[];
    }
  });
}

// All GT Transactions (Admin) - fetches from all users
export function useAllGTTransactions() {
  const { isTestMode } = useTestMode();
  const path = getTestModePath('wallet/transactions', isTestMode);

  return useQuery({
    queryKey: ['allGTTransactions', isTestMode],
    queryFn: async () => {
      const snapshot = await dbGet(path);
      const data = snapshot.val();
      if (!data) return [];

      // Flatten transactions from all users
      const transactions: (GTTransaction & { userId: string })[] = [];
      for (const [userId, userTransactions] of Object.entries(data)) {
        if (userTransactions && typeof userTransactions === 'object') {
          for (const tx of Object.values(userTransactions as Record<string, GTTransaction>)) {
            transactions.push({ ...tx, userId });
          }
        }
      }
      // Sort by createdAt descending
      return transactions.sort((a, b) => b.createdAt - a.createdAt);
    }
  });
}

// Admin Stats
export function useAdminStats(date?: string) {
  const { isTestMode } = useTestMode();
  const targetDate = date || new Date().toISOString().split('T')[0];
  const path = getTestModePath(`adminStats/${targetDate}`, isTestMode);

  return useQuery({
    queryKey: ['adminStats', targetDate, isTestMode],
    queryFn: async () => {
      const snapshot = await dbGet(path);
      return snapshot.val() as AdminStats | null;
    }
  });
}

// Reports
export function useReports(status?: string) {
  const { isTestMode } = useTestMode();
  const reportsPath = getTestModePath('reports', isTestMode);
  const reportsByStatusPath = status ? getTestModePath(`reports/byStatus/${status}`, isTestMode) : '';

  return useQuery({
    queryKey: ['reports', status, isTestMode],
    queryFn: async () => {
      if (status) {
        const snapshot = await dbGet(reportsByStatusPath);
        const reportIds = snapshot.val();
        if (!reportIds) return [];

        const reports: Report[] = [];
        for (const reportId of Object.keys(reportIds)) {
          const reportSnapshot = await dbGet(getTestModePath(`reports/${reportId}`, isTestMode));
          if (reportSnapshot.exists()) {
            reports.push(reportSnapshot.val());
          }
        }
        return reports;
      }

      const snapshot = await dbGet(reportsPath);
      const data = snapshot.val();
      if (!data) return [];
      return Object.values(data) as Report[];
    }
  });
}

// Store Items
export function useStoreItems() {
  const { isTestMode } = useTestMode();
  const path = getTestModePath('store/items', isTestMode);

  return useQuery({
    queryKey: ['storeItems', isTestMode],
    queryFn: async () => {
      const snapshot = await dbGet(path);
      const data = snapshot.val();
      if (!data) return [];
      return Object.values(data) as StoreItem[];
    }
  });
}

// Store Redemptions
export function useStoreRedemptions(status?: string) {
  const { isTestMode } = useTestMode();
  const redemptionsPath = getTestModePath('store/redemptions', isTestMode);
  const redemptionsByStatusPath = status ? getTestModePath(`store/redemptionsByStatus/${status}`, isTestMode) : '';

  return useQuery({
    queryKey: ['storeRedemptions', status, isTestMode],
    queryFn: async () => {
      if (status) {
        const snapshot = await dbGet(redemptionsByStatusPath);
        const redemptionIds = snapshot.val();
        if (!redemptionIds) return [];

        const redemptions: StoreRedemption[] = [];
        for (const redemptionId of Object.keys(redemptionIds)) {
          const redemptionSnapshot = await dbGet(getTestModePath(`store/redemptions/${redemptionId}`, isTestMode));
          if (redemptionSnapshot.exists()) {
            redemptions.push(redemptionSnapshot.val());
          }
        }
        return redemptions;
      }

      const snapshot = await dbGet(redemptionsPath);
      const data = snapshot.val();
      if (!data) return [];
      return Object.values(data) as StoreRedemption[];
    }
  });
}

// Mutations
export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { isTestMode } = useTestMode();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: Partial<User> }) => {
      await dbUpdate(getTestModePath(`users/profiles/${userId}`, isTestMode), {
        ...data,
        updatedAt: Date.now()
      });
    },
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });
}

export function useBanUser() {
  const queryClient = useQueryClient();
  const { isTestMode } = useTestMode();

  return useMutation({
    mutationFn: async ({
      userId,
      reason,
      bannedBy,
      expiresAt
    }: {
      userId: string;
      reason: string;
      bannedBy: string;
      expiresAt?: number;
    }) => {
      await dbUpdate(getTestModePath(`users/profiles/${userId}`, isTestMode), {
        isBanned: true,
        banReason: reason,
        bannedAt: Date.now(),
        bannedBy,
        banExpiresAt: expiresAt || null,
        updatedAt: Date.now()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });
}

export function useUnbanUser() {
  const queryClient = useQueryClient();
  const { isTestMode } = useTestMode();

  return useMutation({
    mutationFn: async (userId: string) => {
      await dbUpdate(getTestModePath(`users/profiles/${userId}`, isTestMode), {
        isBanned: false,
        banReason: null,
        bannedAt: null,
        bannedBy: null,
        banExpiresAt: null,
        updatedAt: Date.now()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });
}

export function useAdjustGT() {
  const queryClient = useQueryClient();
  const { isTestMode } = useTestMode();

  return useMutation({
    mutationFn: async ({
      userId,
      amount,
      description,
      adminId
    }: {
      userId: string;
      amount: number;
      description: string;
      adminId: string;
    }) => {
      // Get current wallet
      const walletSnapshot = await dbGet(getTestModePath(`wallet/balances/${userId}`, isTestMode));
      const wallet = walletSnapshot.val() as GTWallet;

      if (!wallet) {
        throw new Error('Wallet not found');
      }

      const newBalance = wallet.balance + amount;
      if (newBalance < 0) {
        throw new Error('Cannot reduce balance below 0');
      }

      // Create transaction
      const transactionId = `tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const transaction: GTTransaction = {
        transactionId,
        type: 'admin_adjustment',
        amount,
        balanceBefore: wallet.balance,
        balanceAfter: newBalance,
        referenceId: adminId,
        referenceType: 'admin',
        description,
        metadata: { adjustedBy: adminId },
        createdAt: Date.now(),
        processedBy: adminId
      };

      // Update wallet
      await dbUpdate(getTestModePath(`wallet/balances/${userId}`, isTestMode), {
        balance: newBalance,
        totalEarned: amount > 0 ? wallet.totalEarned + amount : wallet.totalEarned,
        totalSpent: amount < 0 ? wallet.totalSpent + Math.abs(amount) : wallet.totalSpent,
        lastUpdated: Date.now(),
        version: wallet.version + 1
      });

      // Save transaction
      await dbSet(getTestModePath(`wallet/transactions/${userId}/${transactionId}`, isTestMode), transaction);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allGTWallets'] });
      queryClient.invalidateQueries({ queryKey: ['gtTransactions'] });
    }
  });
}

export function useResolveReport() {
  const queryClient = useQueryClient();
  const { isTestMode } = useTestMode();

  return useMutation({
    mutationFn: async ({
      reportId,
      action,
      actionNotes,
      reviewedBy,
      reviewedByUsername
    }: {
      reportId: string;
      action: 'none' | 'removed' | 'warned' | 'banned';
      actionNotes: string;
      reviewedBy: string;
      reviewedByUsername: string;
    }) => {
      await dbUpdate(getTestModePath(`reports/${reportId}`, isTestMode), {
        status: 'resolved',
        action,
        actionNotes,
        reviewedBy,
        reviewedByUsername,
        resolvedAt: Date.now()
      });

      // Update index
      const reportSnapshot = await dbGet(getTestModePath(`reports/${reportId}`, isTestMode));
      const report = reportSnapshot.val() as Report;
      if (report) {
        await dbRemove(getTestModePath(`reports/byStatus/${report.status}/${reportId}`, isTestMode));
        await dbSet(getTestModePath(`reports/byStatus/resolved/${reportId}`, isTestMode), Date.now());
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });
}

export function useCreateStoreItem() {
  const queryClient = useQueryClient();
  const { isTestMode } = useTestMode();

  return useMutation({
    mutationFn: async (item: Omit<StoreItem, 'itemId' | 'createdAt' | 'updatedAt'>) => {
      const itemId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const now = Date.now();

      const newItem: StoreItem = {
        ...item,
        itemId,
        createdAt: now,
        updatedAt: now
      };

      await dbSet(getTestModePath(`store/items/${itemId}`, isTestMode), newItem);
      await dbSet(getTestModePath(`store/itemsByCategory/${item.category}/${itemId}`, isTestMode), item.sortOrder);

      return newItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storeItems'] });
    }
  });
}

export function useUpdateStoreItem() {
  const queryClient = useQueryClient();
  const { isTestMode } = useTestMode();

  return useMutation({
    mutationFn: async ({ itemId, data }: { itemId: string; data: Partial<StoreItem> }) => {
      await dbUpdate(getTestModePath(`store/items/${itemId}`, isTestMode), {
        ...data,
        updatedAt: Date.now()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storeItems'] });
    }
  });
}

export function useFulfillRedemption() {
  const queryClient = useQueryClient();
  const { isTestMode } = useTestMode();

  return useMutation({
    mutationFn: async ({
      redemptionId,
      fulfillmentCode,
      trackingNumber,
      trackingCarrier,
      adminNotes,
      fulfilledBy
    }: {
      redemptionId: string;
      fulfillmentCode?: string;
      trackingNumber?: string;
      trackingCarrier?: string;
      adminNotes?: string;
      fulfilledBy: string;
    }) => {
      const updates: Record<string, any> = {
        status: 'fulfilled',
        fulfilledAt: Date.now(),
        fulfilledBy
      };

      if (fulfillmentCode) updates.fulfillmentCode = fulfillmentCode;
      if (trackingNumber) updates.trackingNumber = trackingNumber;
      if (trackingCarrier) updates.trackingCarrier = trackingCarrier;
      if (adminNotes) updates.adminNotes = adminNotes;

      await dbUpdate(getTestModePath(`store/redemptions/${redemptionId}`, isTestMode), updates);

      // Also update user's copy
      const redemptionSnap = await dbGet(getTestModePath(`store/redemptions/${redemptionId}`, isTestMode));
      const redemption = redemptionSnap.val();
      if (redemption?.userId) {
        await dbUpdate(getTestModePath(`store/userRedemptions/${redemption.userId}/${redemptionId}`, isTestMode), updates);
      }

      // Update index
      await dbRemove(getTestModePath(`store/redemptionsByStatus/pending/${redemptionId}`, isTestMode));
      await dbRemove(getTestModePath(`store/redemptionsByStatus/processing/${redemptionId}`, isTestMode));
      await dbSet(getTestModePath(`store/redemptionsByStatus/fulfilled/${redemptionId}`, isTestMode), Date.now());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storeRedemptions'] });
    }
  });
}

export function useUpdateRedemptionStatus() {
  const queryClient = useQueryClient();
  const { isTestMode } = useTestMode();

  return useMutation({
    mutationFn: async ({
      redemptionId,
      status,
      adminNotes
    }: {
      redemptionId: string;
      status: 'processing' | 'pending';
      adminNotes?: string;
    }) => {
      const updates: Record<string, any> = {
        status,
        processedAt: status === 'processing' ? Date.now() : null
      };
      if (adminNotes !== undefined) updates.adminNotes = adminNotes;

      await dbUpdate(getTestModePath(`store/redemptions/${redemptionId}`, isTestMode), updates);

      // Also update user's copy
      const redemptionSnap = await dbGet(getTestModePath(`store/redemptions/${redemptionId}`, isTestMode));
      const redemption = redemptionSnap.val();
      if (redemption?.userId) {
        await dbUpdate(getTestModePath(`store/userRedemptions/${redemption.userId}/${redemptionId}`, isTestMode), updates);
      }

      // Update indexes
      await dbRemove(getTestModePath(`store/redemptionsByStatus/pending/${redemptionId}`, isTestMode));
      await dbRemove(getTestModePath(`store/redemptionsByStatus/processing/${redemptionId}`, isTestMode));
      await dbSet(getTestModePath(`store/redemptionsByStatus/${status}/${redemptionId}`, isTestMode), Date.now());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storeRedemptions'] });
    }
  });
}

export function useCancelRedemption() {
  const queryClient = useQueryClient();
  const { isTestMode } = useTestMode();

  return useMutation({
    mutationFn: async ({
      redemptionId,
      cancelReason,
      cancelledBy,
      refundGT
    }: {
      redemptionId: string;
      cancelReason: string;
      cancelledBy: string;
      refundGT: boolean;
    }) => {
      // Get the redemption first
      const redemptionSnap = await dbGet(getTestModePath(`store/redemptions/${redemptionId}`, isTestMode));
      const redemption = redemptionSnap.val() as StoreRedemption;

      if (!redemption) {
        throw new Error('Redemption not found');
      }

      const now = Date.now();
      const updates: Record<string, any> = {
        status: 'cancelled',
        cancelReason,
        cancelledBy,
        cancelledAt: now
      };

      // Update redemption
      await dbUpdate(getTestModePath(`store/redemptions/${redemptionId}`, isTestMode), updates);

      // Update user's copy
      if (redemption.userId) {
        await dbUpdate(getTestModePath(`store/userRedemptions/${redemption.userId}/${redemptionId}`, isTestMode), updates);
      }

      // Update indexes
      await dbRemove(getTestModePath(`store/redemptionsByStatus/pending/${redemptionId}`, isTestMode));
      await dbRemove(getTestModePath(`store/redemptionsByStatus/processing/${redemptionId}`, isTestMode));
      await dbSet(getTestModePath(`store/redemptionsByStatus/cancelled/${redemptionId}`, isTestMode), now);

      // Refund GT if requested
      if (refundGT && redemption.gtSpent > 0) {
        const walletSnap = await dbGet(getTestModePath(`wallet/balances/${redemption.userId}`, isTestMode));
        const wallet = walletSnap.val() as GTWallet;

        if (wallet) {
          const newBalance = wallet.balance + redemption.gtSpent;
          const transactionId = `tx_${now}_${Math.random().toString(36).substr(2, 9)}`;

          // Update wallet
          await dbUpdate(getTestModePath(`wallet/balances/${redemption.userId}`, isTestMode), {
            balance: newBalance,
            totalEarned: wallet.totalEarned + redemption.gtSpent,
            lastUpdated: now,
            version: wallet.version + 1
          });

          // Create refund transaction
          const transaction: GTTransaction = {
            transactionId,
            type: 'refund',
            amount: redemption.gtSpent,
            balanceBefore: wallet.balance,
            balanceAfter: newBalance,
            referenceId: redemptionId,
            referenceType: 'store_refund',
            description: `Refund for cancelled order: ${redemption.itemName}`,
            metadata: {
              itemId: redemption.itemId,
              itemName: redemption.itemName,
              cancelReason
            },
            createdAt: now,
            processedBy: cancelledBy
          };

          await dbSet(getTestModePath(`wallet/transactions/${redemption.userId}/${transactionId}`, isTestMode), transaction);
        }
      }

      // Release reserved stock if applicable
      const itemSnap = await dbGet(getTestModePath(`store/items/${redemption.itemId}`, isTestMode));
      const item = itemSnap.val() as StoreItem;
      if (item && item.stockReserved > 0) {
        await dbUpdate(getTestModePath(`store/items/${redemption.itemId}`, isTestMode), {
          stockReserved: Math.max(0, item.stockReserved - 1)
        });
      }

      return { refunded: refundGT, amount: redemption.gtSpent };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storeRedemptions'] });
      queryClient.invalidateQueries({ queryKey: ['storeItems'] });
      queryClient.invalidateQueries({ queryKey: ['allGTWallets'] });
    }
  });
}

// Token Packages (GT Bundles) - for wallet page (uses production packages only)
export function useTokenPackages() {
  const [packages, setPackages] = useState<TokenPackage[]>(DEFAULT_TOKEN_PACKAGES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Always use production packages for wallet page (customer-facing)
    const unsubscribe = subscribeToPath(
      `${PROD_PREFIX}/wallet/packages`,
      (data) => {
        if (data) {
          const pkgs = Object.values(data) as TokenPackage[];
          // Sort by sortOrder and filter active packages
          setPackages(pkgs.filter(p => p.isActive).sort((a, b) => a.sortOrder - b.sortOrder));
        } else {
          // Use defaults if no packages in Firebase
          setPackages(DEFAULT_TOKEN_PACKAGES);
        }
        setLoading(false);
      },
      () => {
        // On error, use defaults
        setPackages(DEFAULT_TOKEN_PACKAGES);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { packages, loading };
}

// All Token Packages (for admin, including inactive) - test mode aware
export function useAllTokenPackages() {
  const { isTestMode } = useTestMode();
  const [packages, setPackages] = useState<TokenPackage[]>([]);
  const [loading, setLoading] = useState(true);

  const path = getTestModePath('wallet/packages', isTestMode);

  useEffect(() => {
    const unsubscribe = subscribeToPath(
      path,
      (data) => {
        if (data) {
          const pkgs = Object.values(data) as TokenPackage[];
          setPackages(pkgs.sort((a, b) => a.sortOrder - b.sortOrder));
        } else {
          setPackages([]);
        }
        setLoading(false);
      },
      () => {
        setPackages([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [path]);

  return { packages, loading, isTestMode };
}

// ============================================
// Slip Verification Hooks
// ============================================

// Helper to enrich submission with user data
async function enrichSubmissionWithUserData(submission: SlipSubmission, isTestMode: boolean = false): Promise<SlipSubmission> {
  // If username already exists (legacy data), skip fetch
  if (submission.username) {
    return submission;
  }

  try {
    const userSnap = await dbGet(getTestModePath(`users/profiles/${submission.userId}`, isTestMode));
    const userData = userSnap.val();
    if (userData) {
      return {
        ...submission,
        username: userData.username || 'Unknown',
        userPhotoURL: userData.photoURL || '',
      };
    }
  } catch (err) {
    console.error('Error fetching user data for submission:', submission.submissionId, err);
  }

  return {
    ...submission,
    username: 'Unknown',
    userPhotoURL: '',
  };
}

// Get all slip submissions with optional status filter (test mode aware)
export function useSlipSubmissions(status?: SubmissionStatus) {
  const { isTestMode } = useTestMode();
  const [submissions, setSubmissions] = useState<SlipSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const path = status
    ? getTestModePath(`slips/byStatus/${status}`, isTestMode)
    : getTestModePath('slips/submissions', isTestMode);

  useEffect(() => {
    setLoading(true);

    if (status) {
      // Listening to index, need to fetch full submissions
      const unsubscribe = subscribeToPath(
        path,
        async (data) => {
          if (data) {
            const submissionIds = Object.keys(data);
            const submissionsPath = getTestModePath('slips/submissions', isTestMode);

            // Fetch full submission data for each ID and enrich with user data
            const fetchedSubmissions: SlipSubmission[] = [];
            for (const id of submissionIds) {
              const snap = await dbGet(`${submissionsPath}/${id}`);
              if (snap.val()) {
                const enriched = await enrichSubmissionWithUserData(snap.val() as SlipSubmission, isTestMode);
                fetchedSubmissions.push(enriched);
              }
            }

            // Sort by createdAt descending (newest first)
            setSubmissions(fetchedSubmissions.sort((a, b) => b.createdAt - a.createdAt));
          } else {
            setSubmissions([]);
          }
          setLoading(false);
        },
        (err) => {
          setError(err);
          setLoading(false);
        }
      );
      return () => unsubscribe();
    } else {
      // Get all submissions directly
      const unsubscribe = subscribeToPath(
        path,
        async (data) => {
          if (data) {
            const subs = Object.values(data) as SlipSubmission[];
            // Enrich each submission with user data
            const enrichedSubs = await Promise.all(subs.map(sub => enrichSubmissionWithUserData(sub, isTestMode)));
            setSubmissions(enrichedSubs.sort((a, b) => b.createdAt - a.createdAt));
          } else {
            setSubmissions([]);
          }
          setLoading(false);
        },
        (err) => {
          setError(err);
          setLoading(false);
        }
      );
      return () => unsubscribe();
    }
  }, [path, status, isTestMode]);

  return { submissions, loading, error };
}

// Get a single slip submission
export function useSlipSubmission(submissionId: string | null) {
  const { isTestMode } = useTestMode();
  const path = submissionId ? getTestModePath(`slips/submissions/${submissionId}`, isTestMode) : '';

  return useRealtimeData<SlipSubmission>(path, !!submissionId);
}

// Get verification stats
export function useVerificationStats() {
  const { isTestMode } = useTestMode();
  const today = new Date().toISOString().split('T')[0];
  const path = getTestModePath(`verificationStats/${today}`, isTestMode);

  return useRealtimeData<{
    totalSubmissions: number;
    pending: number;
    approved: number;
    rejected: number;
    flagged: number;
  }>(path, true);
}

// Create a new slip submission (for test upload)
export function useCreateSlipSubmission() {
  const queryClient = useQueryClient();
  const { isTestMode, getPath } = useTestMode();

  return useMutation({
    mutationFn: async ({
      userId,
      username,
      userPhotoURL,
      imageURL,
      source = 'test'
    }: {
      userId: string;
      username: string;
      userPhotoURL: string;
      imageURL: string;
      source?: 'app' | 'web' | 'test';
    }) => {
      const submissionId = `sub_${Date.now()}_${userId.substring(0, 8)}`;
      const now = Date.now();

      const submission: SlipSubmission = {
        submissionId,
        userId,
        username,
        userPhotoURL,
        imageURL,
        imageHash: '', // Would be set by Cloud Function in production
        status: 'pending',
        priority: 0,
        source,
        createdAt: now,
        updatedAt: now
      };

      // Save submission
      await dbSet(getPath(`slips/submissions/${submissionId}`), submission);

      // Update status index
      await dbSet(getPath(`slips/byStatus/pending/${submissionId}`), now);

      // Update user index
      await dbSet(getPath(`slips/byUser/${userId}/${submissionId}`), now);

      return submission;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slipSubmissions'] });
    }
  });
}

// Approve a slip submission
export function useApproveSlipSubmission() {
  const queryClient = useQueryClient();
  const { getPath } = useTestMode();

  return useMutation({
    mutationFn: async ({
      submissionId,
      reviewedBy,
      reviewedByUsername,
      sportsbook,
      betType,
      totalOdds,
      wagerAmount,
      potentialPayout,
      picks,
      notes
    }: {
      submissionId: string;
      reviewedBy: string;
      reviewedByUsername: string;
      sportsbook: string;
      betType: string;
      totalOdds: string;
      wagerAmount: string;
      potentialPayout: string;
      picks: Array<{
        sport: string;
        league: string;
        event: string;
        selection: string;
        odds: string;
        gameTime: number | null;
      }>;
      notes: string;
    }) => {
      const now = Date.now();
      const slipId = `slip_${now}_${submissionId.split('_')[2]}`;

      // Get submission data
      const submissionSnap = await dbGet(getPath(`slips/submissions/${submissionId}`));
      const submission = submissionSnap.val() as SlipSubmission;

      if (!submission) {
        throw new Error('Submission not found');
      }

      // Create the verified slip
      const slip = {
        slipId,
        userId: submission.userId,
        username: submission.username,
        userPhotoURL: submission.userPhotoURL,
        type: 'screenshot',
        screenshotURL: submission.imageURL,
        picks,
        analysis: notes || null,
        confidence: 100,
        status: 'pending', // Slip outcome status (not verification status)
        isVerified: true,
        verifiedBy: reviewedBy,
        verifiedAt: now,
        sweatRoomId: `room_${slipId}`,
        tags: [],
        createdAt: now,
        settledAt: null,
        isArchived: false
      };

      // Update submission with approval
      const updates: Record<string, any> = {};

      // Create verified slip
      updates[getPath(`slips/virtual/${submission.userId}/${slipId}`)] = slip;

      // Update submission status
      updates[getPath(`slips/submissions/${submissionId}/status`)] = 'approved';
      updates[getPath(`slips/submissions/${submissionId}/updatedAt`)] = now;
      updates[getPath(`slips/submissions/${submissionId}/manualReview`)] = {
        reviewedBy,
        reviewedByUsername,
        reviewedAt: now,
        sportsbook,
        betType,
        totalOdds,
        wagerAmount,
        potentialPayout,
        picks,
        notes
      };
      updates[getPath(`slips/submissions/${submissionId}/resolution`)] = {
        decision: 'approved',
        reason: null,
        resolvedBy: reviewedBy,
        resolvedByUsername: reviewedByUsername,
        resolvedAt: now,
        createdSlipId: slipId
      };

      // Update status indexes - remove from all possible previous statuses
      updates[getPath(`slips/byStatus/pending/${submissionId}`)] = null;
      updates[getPath(`slips/byStatus/flagged/${submissionId}`)] = null;
      updates[getPath(`slips/byStatus/processing/${submissionId}`)] = null;
      updates[getPath(`slips/byStatus/approved/${submissionId}`)] = now;

      // User slips index
      updates[getPath(`slips/byUser/${submission.userId}/${slipId}`)] = now;

      console.log('Approve updates:', updates);
      try {
        await dbUpdate('/', updates);
      } catch (updateError) {
        console.error('dbUpdate error:', updateError);
        throw updateError;
      }

      return { slipId, submissionId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slipSubmissions'] });
      queryClient.invalidateQueries({ queryKey: ['slips'] });
    },
    onError: (error) => {
      console.error('Approve mutation error:', error);
    }
  });
}

// Reject a slip submission
export function useRejectSlipSubmission() {
  const queryClient = useQueryClient();
  const { getPath } = useTestMode();

  return useMutation({
    mutationFn: async ({
      submissionId,
      reviewedBy,
      reviewedByUsername,
      reason,
      notes
    }: {
      submissionId: string;
      reviewedBy: string;
      reviewedByUsername: string;
      reason: string;
      notes?: string;
    }) => {
      const now = Date.now();

      const updates: Record<string, any> = {};

      updates[getPath(`slips/submissions/${submissionId}/status`)] = 'rejected';
      updates[getPath(`slips/submissions/${submissionId}/updatedAt`)] = now;
      // adminMessage is used by Cloud Functions for user notification
      updates[getPath(`slips/submissions/${submissionId}/adminMessage`)] = reason;
      updates[getPath(`slips/submissions/${submissionId}/resolution`)] = {
        decision: 'rejected',
        reason,
        resolvedBy: reviewedBy,
        resolvedByUsername: reviewedByUsername,
        resolvedAt: now,
        createdSlipId: null
      };

      if (notes) {
        updates[getPath(`slips/submissions/${submissionId}/manualReview/notes`)] = notes;
      }

      // Update status indexes - remove from all possible previous statuses
      updates[getPath(`slips/byStatus/pending/${submissionId}`)] = null;
      updates[getPath(`slips/byStatus/flagged/${submissionId}`)] = null;
      updates[getPath(`slips/byStatus/processing/${submissionId}`)] = null;
      updates[getPath(`slips/byStatus/rejected/${submissionId}`)] = now;

      console.log('Reject updates:', updates);
      try {
        await dbUpdate('/', updates);
      } catch (updateError) {
        console.error('dbUpdate error:', updateError);
        throw updateError;
      }

      return { submissionId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slipSubmissions'] });
    },
    onError: (error) => {
      console.error('Reject mutation error:', error);
    }
  });
}
